import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Lazy-loaded pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyPhone = lazy(() => import('./pages/VerifyPhone'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Withdraw = lazy(() => import('./pages/Withdraw'));
const Send = lazy(() => import('./pages/Send'));
const History = lazy(() => import('./pages/History'));
const KYC = lazy(() => import('./pages/KYC'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminTransactions = lazy(() => import('./pages/admin/AdminTransactions'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminKYC = lazy(() => import('./pages/admin/AdminKYC'));
const AdminCompliance = lazy(() => import('./pages/admin/AdminCompliance'));
const AdminRates = lazy(() => import('./pages/admin/AdminRates'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30000, refetchOnWindowFocus: false },
  },
});

const PrivateRoute = ({ children, roles }) => {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const Loader = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0A1628' }}>
    <div className="ayael-loader" />
  </div>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { background: '#1A2744', color: '#fff', border: '1px solid #D4A017' },
            success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
          }}
        />
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/verify-phone" element={<VerifyPhone />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* User */}
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/withdraw" element={<PrivateRoute><Withdraw /></PrivateRoute>} />
            <Route path="/send" element={<PrivateRoute><Send /></PrivateRoute>} />
            <Route path="/history" element={<PrivateRoute><History /></PrivateRoute>} />
            <Route path="/kyc" element={<PrivateRoute><KYC /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<PrivateRoute roles={['admin','superadmin','compliance']}><AdminDashboard /></PrivateRoute>} />
            <Route path="/admin/users" element={<PrivateRoute roles={['admin','superadmin']}><AdminUsers /></PrivateRoute>} />
            <Route path="/admin/transactions" element={<PrivateRoute roles={['admin','superadmin','compliance']}><AdminTransactions /></PrivateRoute>} />
            <Route path="/admin/withdrawals" element={<PrivateRoute roles={['admin','superadmin']}><AdminWithdrawals /></PrivateRoute>} />
            <Route path="/admin/kyc" element={<PrivateRoute roles={['admin','superadmin','compliance']}><AdminKYC /></PrivateRoute>} />
            <Route path="/admin/compliance" element={<PrivateRoute roles={['compliance','admin','superadmin']}><AdminCompliance /></PrivateRoute>} />
            <Route path="/admin/rates" element={<PrivateRoute roles={['superadmin']}><AdminRates /></PrivateRoute>} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
